"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";


const caseStudies = [
  {
    index: 0,
    tag: "Case Study 01",
    title: "Food Ordering App and Web",
    desc: "From initial concept to deployment, we designed and engineered a high-performance food ordering ecosystem. Our focus was on streamlining user flows for optimal conversions, integrating live order dispatch metrics, and crafting a feature-rich vendor portal for seamless real-time management.",
    link: "/projects/fot",
    image: "/assets/img/FOTWW.png",
    stats: [
      { value: "40%", label: "Increase in website traffic" },
      { value: "70%", label: "Target keywords ranked" }
    ]
  },
  {
    index: 1,
    tag: "Case Study 02",
    title: "Local Store to E-Commerce Business",
    desc: "Helped a brick-and-mortar retailer transition into a successful digital brand. By designing custom Shopify themes, refining product discovery features, and implementing a quick-loading checkout process, we helped unlock global markets and new digital revenue streams.",
    link: "/projects/dakzy",
    image: "/assets/img/projects/projects/dakzy/Dakzy cover.png",
    stats: [
      { value: "60%", label: "Increase in organic traffic" },
      { value: "#1", label: "Rank on Google" }
    ]
  },
  {
    index: 2,
    tag: "Case Study 03",
    title: "How Girls Slay Grew Its Clientele",
    desc: "Developed a comprehensive digital booking and marketing platform to scale client acquisition. We focused on local SEO optimizations, targeted advertising strategies, and streamlined online booking workflows that drove high conversion and client retention rates.",
    link: "/projects/girlsslay",
    image: "/assets/img/projects/projects/girlsslay/girls-slay-cover.webp",
    stats: [
      { value: "30%", label: "New client bookings" },
      { value: "50%", label: "Local search increase" }
    ]
  },
  {
    index: 3,
    tag: "Case Study 04",
    title: "One Stop Shopping solution With Easy Access",
    desc: "Built a sleek, search-optimized storefront designed for immediate product discovery. We engineered a ultra-lightweight frontend that slashed bounce rates, resulting in a dramatic rise in conversion percentages and search engine visibility.",
    link: "/projects/myacebazar",
    image: "/assets/img/projects/projects/myacebazar/yacebazar.webp",
    stats: [
      { value: "60%", label: "Increase in SEO performance" },
      { value: "80%", label: "Conversion rate" }
    ]
  }
];

const DEFAULT_FALLBACK_IMAGES = [
  "/assets/img/FOTWW.png",
  "/assets/img/projects/projects/dakzy/Dakzy cover.png",
  "/assets/img/projects/projects/girlsslay/girls-slay-cover.webp",
  "/assets/img/projects/projects/myacebazar/yacebazar.webp"
];

function SafeProjectImage({ src, fallbackSrc, alt, priority, sizes, ...props }) {
  const [imgSrc, setImgSrc] = useState(src || fallbackSrc);

  useEffect(() => {
    setImgSrc(src || fallbackSrc);
  }, [src, fallbackSrc]);

  return (
    <Image
      {...props}
      sizes={sizes || "(max-width: 991px) 100vw, (max-width: 1400px) 55vw, 650px"}
      src={imgSrc}
      alt={alt || "Case Study Project"}
      priority={priority}
      onError={() => {
        if (imgSrc !== fallbackSrc && fallbackSrc) {
          setImgSrc(fallbackSrc);
        }
      }}
    />
  );
}

export default function CaseStudiesScroll({ title, projects }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    import("./CaseStudiesScroll.css");
  }, []);

  const getImageUrl = (imgProp, defaultFallback) => {
    if (!imgProp) return defaultFallback;
    let url = typeof imgProp === "string" ? imgProp : (imgProp.url || imgProp.path || imgProp.secure_url || "");
    if (!url || typeof url !== "string") return defaultFallback;
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;

    let finalUrl = url;
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'http://127.0.0.1:5002').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";

    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      finalUrl = `${apiUrl}${cleanPath}`;
    } else if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      finalUrl = `${apiUrl}${cleanPath}`;
    } else if (url.startsWith("http")) {
      finalUrl = url;
    } else if (url.startsWith("/")) {
      finalUrl = url;
    }

    if (finalUrl.includes("res.cloudinary.com") && finalUrl.includes("/image/upload/")) {
      if (!finalUrl.includes("f_auto") && !finalUrl.includes("q_auto")) {
        finalUrl = finalUrl.replace("/image/upload/", "/image/upload/f_auto,q_auto/");
      }
    }
    return finalUrl || defaultFallback;
  };

  const normalizeLink = (rawLink, fallbackLink) => {
    const linkStr = rawLink || fallbackLink || "/projects";
    if (linkStr.startsWith("http://") || linkStr.startsWith("https://") || linkStr.startsWith("#")) {
      return linkStr;
    }
    const cleanPath = linkStr.startsWith("/") ? linkStr : `/${linkStr}`;
    const knownSlugs = ["myacebazar", "dakzy", "girlsslay", "fot", "mypocketnotes", "anthlete"];
    const slug = cleanPath.replace(/^\//, "");
    if (knownSlugs.includes(slug)) {
      return `/projects/${slug}`;
    }
    if (!cleanPath.startsWith("/projects/") && cleanPath !== "/projects") {
      return `/projects${cleanPath}`;
    }
    return cleanPath;
  };

  const displayCaseStudies = (projects && projects.length > 0)
    ? projects.map((p, idx) => {
      const defaultImg = DEFAULT_FALLBACK_IMAGES[idx % DEFAULT_FALLBACK_IMAGES.length];
      const parsedImg = getImageUrl(p.image, defaultImg);
      const defaultLinks = ["/projects/fot", "/projects/dakzy", "/projects/girlsslay", "/projects/myacebazar"];
      const rawLink = p.button?.link || defaultLinks[idx % defaultLinks.length] || "/projects";
      return {
        index: idx,
        tag: `Case Study 0${idx + 1}`,
        title: p.title,
        desc: p.description,
        link: normalizeLink(rawLink, defaultLinks[idx % defaultLinks.length]),
        image: parsedImg,
        fallbackImage: defaultImg,
        stats: p.stats || []
      };
    })
    : caseStudies;

  useEffect(() => {
    let ticking = false;
    let cachedCards = null;
    let viewportCenter = 0;
    let viewportHeight = 0;

    const updateMetrics = () => {
      if (typeof window === "undefined") return;
      cachedCards = document.querySelectorAll(".case-study-card-content");
      viewportHeight = window.innerHeight;
      viewportCenter = viewportHeight / 2;
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        if (!cachedCards || !cachedCards.length) {
          updateMetrics();
        }
        if (!cachedCards || !cachedCards.length) return;

        let closestIdx = 0;
        let closestDist = Infinity;

        cachedCards.forEach((card, idx) => {
          const rect = card.getBoundingClientRect();
          const cardCenter = rect.top + rect.height / 2;
          const dist = Math.abs(cardCenter - viewportCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = idx;
          }
        });

        if (closestDist > viewportHeight * 0.45) {
          setActiveIndex(-1);
        } else {
          setActiveIndex(closestIdx);
        }
      });
    };

    updateMetrics();
    window.addEventListener("resize", updateMetrics, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("resize", updateMetrics);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [displayCaseStudies]);

  return (
    <section className="case-studies-retool-wrapper py-5">
      <div className="container-fluid case-studie-padding" style={{ maxWidth: "1350px", margin: "0 auto" }}>

        {/* Section Heading */}
        <div className="text-center mb-5" data-aos="fade-up">
          <span className="brands ">works</span>
          <h2 className="mt-3 mb-10 brand-trust">{title || "Case Studies"}</h2>
        </div>

        {/* Sticky Scroll Section Layout */}
        <div className="case-studies-sticky-layout">

          {/* Left Column: Sticky Image */}
          <div className="sticky-image-col" style={{ opacity: activeIndex === -1 ? 0 : 1, transition: "opacity 0.4s ease, transform 0.4s ease", transform: activeIndex === -1 ? "scale(0.95)" : "scale(1)", pointerEvents: activeIndex === -1 ? "none" : "auto" }}>
            <div className="sticky-image-container">
              {displayCaseStudies.map((study, idx) => (
                <div
                  key={study.index}
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: activeIndex === idx ? "auto" : "none",
                    opacity: activeIndex === idx ? 1 : 0,
                    transform: activeIndex === idx ? "scale(1)" : "scale(0.97)",
                    transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
                  }}
                  className={`image-wrapper-card ${activeIndex === idx ? "active" : ""}`}
                >
                  <Link href={study.link} style={{ position: "relative", display: "block", width: "100%", height: "100%" }}>
                    <div className="card-image-wrap" style={{ position: "relative", width: "100%", height: "100%" }}>
                      <SafeProjectImage
                        src={study.image}
                        fallbackSrc={study.fallbackImage || DEFAULT_FALLBACK_IMAGES[idx % DEFAULT_FALLBACK_IMAGES.length]}
                        alt={study.title}
                        fill
                        sizes="(max-width: 991px) 100vw, (max-width: 1400px) 55vw, 650px"
                        className="object-cover shadow-2xl"
                        priority={idx === 0}
                      />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Scrollable Content List */}
          <div className="scrollable-content-col">
            {displayCaseStudies.map((study, idx) => {
              const isActive = activeIndex === idx;
              return (
                <div
                  key={study.index}
                  className={`case-study-card-content ${isActive ? "active" : ""}`}
                  data-index={idx}
                >
                  {/* Mobile-only image that appears inline on small screens */}
                  <div className="mobile-only-image" style={{ position: "relative", width: "100%" }}>
                    <Link href={study.link} style={{ position: "relative", display: "block", width: "100%", height: "100%" }}>
                      <SafeProjectImage
                        src={study.image}
                        fallbackSrc={study.fallbackImage || DEFAULT_FALLBACK_IMAGES[idx % DEFAULT_FALLBACK_IMAGES.length]}
                        alt={study.title}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 991px) 80vw, 500px"
                        className="object-cover"
                      />
                    </Link>
                  </div>

                  <span className="card-head">{study.tag}</span>
                  <h3 className="card-title">{study.title}</h3>
                  <p className="card-text">{study.desc}</p>

                  <div className="stats-row">
                    {study.stats.map((stat, sIdx) => (
                      <div className="work-percent" key={sIdx}>
                        <span className="work-percent-val">{stat.value}</span>
                        <small>{stat.label}</small>
                      </div>
                    ))}
                  </div>

                  <Link href={study.link} className="view-case-study-btn">
                    Explore {study.title} Case Study <span className="arrow-icon">↗</span>
                  </Link>
                </div>
              );
            })}
          </div>

        </div>

      </div>
    </section>
  );
}
